import { useCallback, useEffect, useState } from 'react';
import { Form, message, Modal } from 'antd';
import axios from 'axios';
import ConfigPlanoPropostaService from '../../../services/http/services/Propostas/ConfigPlanoPropostaService';
import ConfiguracaoGeralPropostaService from '../../../services/http/services/Propostas/ConfiguracaoGeralPropostaService';
import { urlContratosPlano, urlContratoSimplesAtivos, urlContratoAtivos, urlUsersStatusAtivos } from '../../../services/request/urls';
import { getToken } from '../../../services/token';
import { IConfigPlano, IConfiguracaoGeralProposta } from '../interface';
import { IUsuarioItem } from '../CadastrarProposta/useCadastrarProposta';
import { EmitirErro } from '../../../services/EmitirErro';
import { notification } from 'antd';
import { useWatch } from 'antd/lib/form/Form';

export const TEXTO_PADRAO_DISPOSITIVO: Record<string, string> = {
    tela: 'TELA INTERATIVA DE 75 POLEGADAS EM COMODATO.\n\nACESSO INCLUSO AOS APLICATIVOS DASHBOARD E GOVFÁCIL.',
    tela75: 'TELA INTERATIVA DE 75 POLEGADAS EM COMODATO.\n\nACESSO INCLUSO AOS APLICATIVOS DASHBOARD E GOVFÁCIL.',
    tela85: 'TELA INTERATIVA DE 85 POLEGADAS EM COMODATO.\n\nACESSO INCLUSO AOS APLICATIVOS DASHBOARD E GOVFÁCIL.',
    totem: 'TOTEM INTERATIVO DE LED 32 POLEGADAS COM SUPORTE.\n\nACESSO INCLUSO AOS APLICATIVOS DASHBOARD E GOVFÁCIL.'
};

export const GERAR_DETALHAMENTO_PADRAO = (params?: {
    possuiTela?: boolean;
    tipoTela?: string | null;
    contratacaoHoras?: boolean;
}) => {
    let equipLinhaTexto = '• Concessão de uso de 1 (uma) Tela Interativa de Led 75 polegadas e seu suporte em comodato *por cada licença*, pelo período vigente do contrato.';
    if (params?.tipoTela === 'totem') {
        equipLinhaTexto = '• Concessão de uso de 1 (um) Totem Interativo de Led 32 polegadas e seu suporte em comodato *por cada licença*, pelo período vigente do contrato.';
    } else if (params?.tipoTela === 'tela85') {
        equipLinhaTexto = '• Concessão de uso de 1 (uma) Tela Interativa de Led 85 polegadas e seu suporte em comodato *por cada licença*, pelo período vigente do contrato.';
    }
    const equipLinha = params?.possuiTela !== false ? equipLinhaTexto : '';

    return [
        '• Trata-se de licença para uso da ferramenta de gestão Govfácil App, onde cada licença possui 1 acesso ao aplicativo. As licenças do aplicativo se classificam como contínuas.',
        equipLinha,
        '• Prazo das licenças: *12 MESES*, sendo a renovação automática e faturada conforme proposta a ser enviada 30 dias antes do vencimento.',
        '• Forma de Pagamento: {{forma_pagamento}}',
        '',
        '*VALIDADE PROPOSTA: 60 DIAS.*'
    ].filter(Boolean).join('\n');
};

export const useConfiguracaoPlanos = () => {
    // Tab 1: Modelos de Planos
    const [form] = Form.useForm();
    const [configs, setConfigs] = useState<IConfigPlano[]>([]);
    const [planosBase, setPlanosBase] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [editingConfig, setEditingConfig] = useState<IConfigPlano | null>(null);
    const [contratacaoHoras, setContratacaoHoras] = useState<boolean>(false);

    // Tab 2: Configurações Gerais
    const [formGeral] = Form.useForm();
    const [activeTab, setActiveTab] = useState<string>('planos');
    const [configGeral, setConfigGeral] = useState<IConfiguracaoGeralProposta | null>(null);
    const [loadingGeral, setLoadingGeral] = useState<boolean>(false);
    const [salvandoGeral, setSalvandoGeral] = useState<boolean>(false);
    const [cidadesAtivas, setCidadesAtivas] = useState<{ label: string; value: string }[]>([]);
    const [loadingCidadesAtivas, setLoadingCidadesAtivas] = useState<boolean>(false);
    const [usuarios, setUsuarios] = useState<IUsuarioItem[]>([]);
    const [loadingUsuarios, setLoadingUsuarios] = useState<boolean>(false);

    const tipoTelaForm = useWatch('tipo_tela', form);
    const possuiTelaForm = useWatch('possui_tela', form);

    useEffect(() => {
        if (!possuiTelaForm) return;
        const tipo = tipoTelaForm || 'tela';
        const valorAtual = form.getFieldValue('texto_dispositivo_padrao');
        const padroes = Object.values(TEXTO_PADRAO_DISPOSITIVO);
        if (!valorAtual || padroes.includes(valorAtual)) {
            form.setFieldValue('texto_dispositivo_padrao', TEXTO_PADRAO_DISPOSITIVO[tipo] || TEXTO_PADRAO_DISPOSITIVO.tela);
        }
    }, [tipoTelaForm, possuiTelaForm, form]);

    const carregarDadosPlanos = useCallback(async () => {
        setLoading(true);
        try {
            const [configsData, planosData] = await Promise.all([
                ConfigPlanoPropostaService.getAll(),
                axios.get(urlContratosPlano, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                })
            ]);
            setConfigs(configsData);
            setPlanosBase(planosData.data || []);
        } catch (error) {
            EmitirErro('Erro ao carregar configurações de planos');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const carregarConfigGeral = useCallback(async () => {
        setLoadingGeral(true);
        setLoadingCidadesAtivas(true);
        setLoadingUsuarios(true);
        try {
            const token = getToken();
            const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

            const [geralData, contratosRes, usuariosRes] = await Promise.all([
                ConfiguracaoGeralPropostaService.get().catch(() => null),
                axios.get(urlContratoAtivos, { headers }).catch(() => ({ data: [] })),
                axios.get(urlUsersStatusAtivos, { headers }).catch(() => ({ data: [] }))
            ]);

            const listaUsuarios = Array.isArray(usuariosRes?.data) ? usuariosRes.data : [];
            const contatoPadraoId = geralData?.contato_padrao_id || geralData?.contato_padrao?.id;
            const vendedores = listaUsuarios.filter((u: any) => Boolean(u.vendedor) || (contatoPadraoId && u.id === Number(contatoPadraoId)));
            
            if (geralData?.contato_padrao?.id) {
                const cpId = geralData.contato_padrao.id;
                if (!vendedores.some((u: any) => u.id === cpId)) {
                    vendedores.push(geralData.contato_padrao);
                }
            }

            setUsuarios(vendedores.length > 0 ? vendedores : listaUsuarios);

            if (geralData) {
                setConfigGeral(geralData);
                const municipiosSalvosLimpos = (geralData.municipios_destaque || []).map((m: string) =>
                    (m || '').replace(/^autom[áa]tico\s+/i, '').trim()
                ).filter(Boolean);

                formGeral.setFieldsValue({
                    valor_hora_padrao: geralData.valor_hora_padrao,
                    municipios_destaque: municipiosSalvosLimpos,
                    contato_padrao_id: geralData.contato_padrao_id || geralData.contato_padrao?.id
                });
            }

            const contratosLista = Array.isArray(contratosRes?.data) ? contratosRes.data : [];
            const opcoesCidadesMap = new Map<string, string>();

            contratosLista.forEach((c: any) => {
                let nome = (c.nome || '').trim();
                nome = nome.replace(/^autom[áa]tico\s+/i, '').trim();
                if (nome) {
                    opcoesCidadesMap.set(nome, nome);
                }
            });

            if (geralData?.municipios_destaque) {
                geralData.municipios_destaque.forEach((cidade: string) => {
                    const limpa = (cidade || '').replace(/^autom[áa]tico\s+/i, '').trim();
                    if (limpa && !opcoesCidadesMap.has(limpa)) {
                        opcoesCidadesMap.set(limpa, limpa);
                    }
                });
            }

            const opcoesFormatadas = Array.from(opcoesCidadesMap.keys())
                .sort((a, b) => a.localeCompare(b))
                .map((cidade) => ({
                    label: cidade,
                    value: cidade
                }));

            setCidadesAtivas(opcoesFormatadas);
        } catch (error) {
            EmitirErro('Erro ao carregar configurações gerais');
            console.error(error);
        } finally {
            setLoadingGeral(false);
            setLoadingCidadesAtivas(false);
            setLoadingUsuarios(false);
        }
    }, [formGeral]);

    useEffect(() => {
        carregarDadosPlanos();
        carregarConfigGeral();
    }, [carregarDadosPlanos, carregarConfigGeral]);

    const handleNovo = () => {
        setEditingConfig(null);
        setContratacaoHoras(false);
        form.resetFields();
        form.setFieldsValue({
            contratacao_horas: false,
            possui_tela: true,
            tipo_tela: 'tela',
            texto_dispositivo_padrao: TEXTO_PADRAO_DISPOSITIVO.tela,
            detalhamento_padrao: GERAR_DETALHAMENTO_PADRAO({ possuiTela: true, tipoTela: 'tela', contratacaoHoras: false }),
            is_principal: false
        });
        setModalVisible(true);
    };

    const handleEditar = (config: IConfigPlano) => {
        setEditingConfig(config);
        setContratacaoHoras(config.contratacao_horas);
        const tipoTela = config.tipo_tela || (config.possui_tela ? 'tela' : 'tela');
        form.setFieldsValue({
            plano_id: config.plano_id,
            descricao_plano: config.descricao_plano,
            texto_objeto_padrao: config.texto_objeto_padrao,
            contratacao_horas: config.contratacao_horas,
            possui_tela: config.possui_tela,
            tipo_tela: tipoTela,
            texto_dispositivo_padrao: config.texto_dispositivo_padrao || (config.possui_tela ? (TEXTO_PADRAO_DISPOSITIVO[tipoTela] || TEXTO_PADRAO_DISPOSITIVO.tela) : ''),
            detalhamento_padrao: config.detalhamento_padrao || GERAR_DETALHAMENTO_PADRAO({
                possuiTela: config.possui_tela,
                tipoTela: config.tipo_tela,
                contratacaoHoras: config.contratacao_horas
            }),
            is_principal: Boolean(config.is_principal)
        });
        setModalVisible(true);
    };

    const handleExcluir = (id: number) => {
        Modal.confirm({
            title: 'Excluir Configuração de Plano',
            content: 'Deseja realmente remover esta configuração de plano comercial?',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await ConfigPlanoPropostaService.delete(id);
                    notification.success({
                        message: 'Sucesso!',
                        description: 'Configuração removida com sucesso!'
                    });
                    carregarDadosPlanos();
                } catch (error: any) {
                    const mensagem = error?.response?.data?.message || 'Erro ao excluir configuração de plano.';
                    notification.error({
                        message: 'Não é possível excluir',
                        description: mensagem
                    });
                    console.error(error);
                }
            }
        });
    };

    const handleSalvar = async (values: any) => {
        setSubmitting(true);
        try {
            const payload = {
                ...values,
                contratacao_horas: Boolean(values.contratacao_horas),
                possui_tela: Boolean(values.possui_tela),
                tipo_tela: values.possui_tela ? (values.tipo_tela || 'tela') : null,
                texto_dispositivo_padrao: values.possui_tela ? (values.texto_dispositivo_padrao || null) : null,
                is_principal: Boolean(values.is_principal)
            };

            if (editingConfig) {
                await ConfigPlanoPropostaService.update(editingConfig.id, {
                    ...payload,
                    id: editingConfig.id
                });
                notification.success({
                    message: "Sucesso !",
                    description: 'Configuração atualizada com sucesso!'
                });
            } else {
                await ConfigPlanoPropostaService.create(payload);
                notification.success({
                    message: "Sucesso !",
                    description: 'Configuração criada com sucesso!'
                });
            }

            setModalVisible(false);
            carregarDadosPlanos();
        } catch (error) {
            EmitirErro(error);
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSalvarConfigGeral = async (values: any) => {
        const municipios = Array.isArray(values.municipios_destaque) ? values.municipios_destaque : [];
        if (municipios.length < 20) {
            notification.warning({
                message: 'Validação de Municípios',
                description: `Selecione no mínimo 20 municípios para o Slide 2 (atualmente com ${municipios.length}).`
            });
            return;
        }
        if (municipios.length > 25) {
            notification.warning({
                message: 'Validação de Municípios',
                description: `Selecione no máximo 25 municípios para o Slide 2 (atualmente com ${municipios.length}).`
            });
            return;
        }

        setSalvandoGeral(true);
        try {
            const payload = {
                valor_hora_padrao: Number(values.valor_hora_padrao || 150),
                municipios_destaque: municipios,
                contato_padrao_id: values.contato_padrao_id || null
            };

            const updated = await ConfiguracaoGeralPropostaService.update(payload);
            setConfigGeral(updated);
            formGeral.setFieldsValue({
                valor_hora_padrao: updated.valor_hora_padrao,
                municipios_destaque: updated.municipios_destaque || [],
                contato_padrao_id: updated.contato_padrao_id || updated.contato_padrao?.id || null
            });
            notification.success({
                message: "Sucesso !",
                description: 'Configurações gerais salvas com sucesso!'
            });
        } catch (error) {
            EmitirErro(error);
            console.error(error);
        } finally {
            setSalvandoGeral(false);
        }
    };

    return {
        // Tab 1: Planos
        form,
        configs,
        planosBase,
        loading,
        modalVisible,
        submitting,
        editingConfig,
        contratacaoHoras,
        setContratacaoHoras,
        setModalVisible,
        handleNovo,
        handleEditar,
        handleExcluir,
        handleSalvar,
        carregarDados: carregarDadosPlanos,

        // Tab 2: Configurações Gerais
        formGeral,
        activeTab,
        setActiveTab,
        configGeral,
        loadingGeral,
        salvandoGeral,
        cidadesAtivas,
        loadingCidadesAtivas,
        usuarios: usuarios.filter(u => Boolean(u.vendedor)),
        loadingUsuarios,
        carregarConfigGeral,
        handleSalvarConfigGeral
    };
};
